import type { ReactNode } from "react"

import { clsx } from "clsx"

import type {
  BatteryComputePaymentShareDto,
  BatteryFinancingTermsDto,
} from "~/api/dto/battery-proposal.dto"
import {
  BATTERY_COMPUTE_BONUS,
  BATTERY_COMPUTE_LEDE_UNAVAILABLE,
  BATTERY_COMPUTE_SHARE_NOTE,
  batteryComputeDisclosure,
  batteryComputeFaq,
  batteryComputeHowItWorks,
  batteryComputeLede,
  batteryComputePlanName,
  batteryComputeStory,
  batteryComputeTermNote,
  type BatteryComputePayoutCopy,
} from "~/constants/battery-ai-compute-copy.constants"

import {
  formatComputeSpecs,
  formatNumber,
} from "../../ProposalPresentation/ai-compute/ai-compute-format"
import {
  AiComputeFaq,
  HowItWorksPanel,
  LedgerRow,
  NodeHeroPanel,
  ShareSplit,
  Stat,
  VendorAppCard,
} from "../../ProposalPresentation/ai-compute/AiComputeParts"
import type { BatteryPresentationAiCompute } from "../presentation-proposal"
import { Eyebrow, GradText, Panel, Section, SectionHeading } from "../primitives"
import { formatEscalator } from "../tpo-terms-copy"
import { formatUsd, formatUsdExact } from "../view-helpers"

/** The gateway's Rev 2.10 payout with every figure present. */
type PayoutFigures = {
  planName: string
  monthlyBase: number
  monthlyBonusCap: number
  monthlyMax: number
  termYears: number
  baseTermTotal: number
  maxTermTotal: number
}

const payoutFigures = ({
  payout,
}: BatteryPresentationAiCompute): PayoutFigures | null =>
  payout?.available &&
  payout.plan != null &&
  payout.monthlyBase != null &&
  payout.monthlyBonusCap != null &&
  payout.monthlyMax != null &&
  payout.termYears != null &&
  payout.baseTermTotal != null &&
  payout.maxTermTotal != null
    ? {
        planName: batteryComputePlanName(payout.plan),
        monthlyBase: payout.monthlyBase,
        monthlyBonusCap: payout.monthlyBonusCap,
        monthlyMax: payout.monthlyMax,
        termYears: payout.termYears,
        baseTermTotal: payout.baseTermTotal,
        maxTermTotal: payout.maxTermTotal,
      }
    : null

/** The bonus's swatch: the base's green, faded, so it never reads as paid. */
const BONUS_SWATCH = `bg-bp-success/30`

const Tag = ({
  tone,
  children,
}: {
  tone: `guaranteed` | `varies`
  children: ReactNode
}): JSX.Element => (
  <span
    className={clsx(
      `ml-2 rounded-full px-2 py-0.5 align-middle text-[10.5px] font-semibold tracking-[0.08em] uppercase`,
      tone === `guaranteed`
        ? `bg-bp-success-soft text-bp-success`
        : `bg-bp-elevated text-bp-text-muted`,
    )}
  >
    {children}
  </span>
)

/** "What is a compute node?": why, what it does, who pays and how you earn. */
function ComputeNodeStory({
  copy,
  planName,
}: {
  copy: BatteryComputePayoutCopy | null
  planName: string | null
}): JSX.Element {
  const story = batteryComputeStory(copy, planName)
  return (
    <div data-testid="battery-ai-compute-story" className="mb-3.5">
      <Panel className="rounded-[20px] p-6 lg:p-8">
        <Eyebrow className="mb-3">What is a compute node?</Eyebrow>
        <h3 className="font-display max-w-[820px] text-[length:var(--step-2)] leading-tight font-semibold tracking-[-0.02em] text-bp-text">
          {story.heading}
        </h3>
        <p className="mt-3 max-w-[900px] text-[14.5px] leading-relaxed text-bp-text-muted">
          {story.intro}
        </p>
        <ol
          aria-label="How the money moves"
          className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {story.flow.map(({ who, what, arrow }, index) => (
            <li
              key={who}
              className={clsx(
                `rounded-xl border p-4`,
                index === story.flow.length - 1
                  ? `border-bp-success/40 bg-bp-success-soft`
                  : `border-bp-border bg-bp-elevated`,
              )}
            >
              <div className="text-[13px] font-semibold text-bp-text">{who}</div>
              <div className="mt-1 text-[12.5px] leading-snug text-bp-text-muted">
                {what}
              </div>
              {arrow ? (
                <div className="mt-2 text-[11px] font-semibold tracking-[0.08em] text-bp-accent uppercase">
                  → {arrow}
                </div>
              ) : null}
            </li>
          ))}
        </ol>
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {story.answers.map(({ question, headline, body }) => (
            <div key={question} className="border-t border-bp-border pt-4">
              <Eyebrow>{question}</Eyebrow>
              <div className="font-display mt-1.5 text-[16px] leading-snug font-semibold text-bp-text">
                {headline}
              </div>
              <p className="mt-1.5 text-[13px] leading-relaxed text-bp-text-muted">
                {body}
              </p>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  )
}

/** "How the network bonus works" in plain words: a strong month beside a slow one. */
function NetworkBonus({ figures }: { figures: PayoutFigures }): JSX.Element {
  const base = formatUsd(figures.monthlyBase)
  const months = [
    {
      ...BATTERY_COMPUTE_BONUS.strong,
      bonus: `+ bonus up to ${formatUsd(figures.monthlyBonusCap)}`,
      strong: true,
    },
    { ...BATTERY_COMPUTE_BONUS.slow, bonus: `+ $0 bonus`, strong: false },
  ]
  return (
    <div data-testid="battery-ai-compute-bonus" className="mb-3.5">
      <Panel className="rounded-[20px] p-6 lg:p-8">
        <Eyebrow className="mb-3">How the network bonus works</Eyebrow>
        <p className="max-w-[900px] text-[15px] leading-relaxed text-bp-text-soft">
          {BATTERY_COMPUTE_BONUS.lead}
        </p>
        <div className="mt-5 grid grid-cols-1 gap-3.5 md:grid-cols-2">
          {months.map(({ label, headline, body, bonus, strong }) => (
            <div
              key={label}
              className={clsx(
                `flex flex-col rounded-xl border p-5`,
                strong
                  ? `border-bp-success/40 bg-bp-success-soft`
                  : `border-bp-border bg-bp-elevated`,
              )}
            >
              <Eyebrow>{label}</Eyebrow>
              <div className="font-display mt-1.5 text-[17px] font-semibold text-bp-text">
                {headline}
              </div>
              <p className="mt-1 flex-1 text-[13px] leading-relaxed text-bp-text-muted">
                {body}
              </p>
              <div className="mt-4 flex flex-wrap gap-x-2 border-t border-bp-border pt-3 text-[14px] font-semibold tabular-nums">
                <span className="text-bp-text">{base} base</span>
                <span
                  className={strong ? `text-bp-success` : `text-bp-text-muted`}
                >
                  {bonus}
                </span>
              </div>
            </div>
          ))}
        </div>
        <ul className="mt-5 grid grid-cols-1 gap-3 text-[13px] leading-relaxed text-bp-text-muted md:grid-cols-3">
          {BATTERY_COMPUTE_BONUS.notes.map(({ title, body }) => (
            <li key={title}>
              <span className="font-semibold text-bp-text">{title}</span> {body}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  )
}

/**
 * The payment beside the battery's monthly loan, lease or PPA payment. The
 * base's split is the gateway's; the bonus cap stands beside it and is never
 * added into what's covered or left over. The payment keeps the investment
 * card's qualifiers: a TPO payment is year one's, and a BrightStart payment
 * assumes the deferral is repaid.
 */
function ShareBesidePayment({
  monthlyPayment,
  terms,
  base,
  figures,
}: {
  monthlyPayment: number
  terms: BatteryFinancingTermsDto | null
  base: NonNullable<BatteryComputePaymentShareDto[`base`]>
  figures: PayoutFigures
}): JSX.Element {
  const qualifier =
    terms?.tpo != null
      ? `Your first-year payment; it rises ${formatEscalator(terms.tpo.escalationRate)} each year, so less is left over in later years.`
      : terms?.sungage != null
        ? `Assumes the Deferred Payment Portion is repaid by the reamortization date; otherwise your payment rises to an estimated ${formatUsdExact(terms.sungage.maxMonthlyPayment)}/mo.`
        : null

  const parts = [
    {
      label: `Toward your battery payment`,
      value: base.coveredPayment,
      swatch: `bg-bp-accent`,
    },
    { label: `Base left over`, value: base.leftOver, swatch: `bg-bp-success` },
    {
      label: `Bonus, up to`,
      value: figures.monthlyBonusCap,
      swatch: BONUS_SWATCH,
    },
  ] as const

  return (
    <div data-testid="battery-ai-compute-share" className="flex flex-col gap-4">
      <Eyebrow>Where your payment can go · illustrative</Eyebrow>
      <p className="font-display text-[22px] leading-snug font-medium tracking-[-0.01em] text-bp-text-soft">
        Up to{` `}
        <span className="font-semibold text-bp-success">
          {formatUsd(figures.monthlyMax)}/mo
        </span>
        {` `}— a {formatUsd(base.monthlyBase)} base plus bonus — beside your
        estimated{` `}
        <span className="font-semibold text-bp-text">
          {formatUsdExact(monthlyPayment)}/mo
        </span>
        {` `}
        {terms?.tpo != null ? `first-year battery payment.` : `battery payment.`}
      </p>
      {qualifier ? (
        <p
          data-testid="battery-ai-compute-share-qualifier"
          className="-mt-2 text-[13px] text-bp-text-muted"
        >
          {qualifier}
        </p>
      ) : null}
      <ShareSplit parts={parts} />
      <p className="rounded-xl border border-bp-border bg-bp-elevated px-4 py-3.5 text-[13px] leading-relaxed text-bp-text-muted">
        {BATTERY_COMPUTE_SHARE_NOTE}
      </p>
    </div>
  )
}

/**
 * The compute node section of a Battery-Only presentation: the selected
 * Vendor node and its Hosting Agreement Rev 2.10 payout, read-only and kept
 * apart from the battery's savings and investment. The node is chosen in the
 * AI Compute setup step; an editor's page passes `action` to link there.
 * Every figure is the gateway's, formatted only; the only bonus figure shown
 * is the plan's cap. `paymentShare` sets the base beside the monthly
 * payment; without one (cash, or no current payment) the ledger stands alone.
 */
export function BatteryAiComputeSection({
  aiCompute,
  paymentShare = null,
  action = null,
}: {
  aiCompute: BatteryPresentationAiCompute
  /** The financial summary's split of the base at the monthly payment. */
  paymentShare?: BatteryComputePaymentShareDto | null
  action?: ReactNode
}): JSX.Element {
  const { name, computerData, income } = aiCompute
  const figures = payoutFigures(aiCompute)
  const copy: BatteryComputePayoutCopy | null = figures
    ? {
        base: formatUsd(figures.monthlyBase),
        bonusCap: formatUsd(figures.monthlyBonusCap),
        termYears: figures.termYears,
      }
    : null
  // The split shows only when its base is the ledger's: two reads that
  // disagree show no comparison rather than mismatched figures.
  const besidePayment =
    figures && paymentShare?.base?.monthlyBase === figures.monthlyBase
      ? {
          monthlyPayment: paymentShare.monthlyPayment,
          terms: paymentShare.terms,
          base: paymentShare.base,
          figures,
        }
      : null
  const specs = formatComputeSpecs(computerData)
  const nodeLoad = income.nodeLoadKwhPerYear

  return (
    <Section id="ai-compute">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionHeading
          eyebrow="Optional add-on · Compute node"
          sub={
            copy ? batteryComputeLede(copy) : BATTERY_COMPUTE_LEDE_UNAVAILABLE
          }
        >
          A fixed monthly payment,{` `}
          <GradText>separate from your battery savings</GradText>.
        </SectionHeading>
        {action ? <div className="mb-9">{action}</div> : null}
      </div>

      <div className="mb-3.5 grid grid-cols-1 gap-3.5 lg:grid-cols-[1.45fr_1fr]">
        <NodeHeroPanel
          name={name}
          specs={specs}
          testId="battery-ai-compute-name"
          badge={
            figures ? (
              <span data-testid="battery-ai-compute-plan">
                {figures.planName} Hosting Plan
              </span>
            ) : null
          }
        />
        <HowItWorksPanel
          steps={batteryComputeHowItWorks(copy)}
          footer={figures ? batteryComputeTermNote(figures.termYears) : null}
        />
      </div>

      <ComputeNodeStory copy={copy} planName={figures?.planName ?? null} />

      {figures ? (
        <>
          <NetworkBonus figures={figures} />

          <Panel
            className={clsx(
              `mb-3.5 grid grid-cols-1 gap-10 rounded-[20px] p-6 lg:gap-12 lg:p-8`,
              // One column, the ledger capped, when there is no payment to set it beside.
              besidePayment && `lg:grid-cols-2`,
            )}
          >
            <div
              data-testid="battery-ai-compute-ledger"
              className={besidePayment ? undefined : `max-w-[640px]`}
            >
              <Eyebrow className="mb-4">Your monthly hosting payment</Eyebrow>
              <LedgerRow
                label="Hosting plan"
                note="set in your hosting schedule"
                value={figures.planName}
              />
              <div className="flex items-baseline justify-between gap-4 border-t border-bp-border-strong py-3.5">
                <div>
                  <div className="text-[14.5px] font-medium text-bp-text">
                    Fixed monthly base<Tag tone="guaranteed">Guaranteed</Tag>
                  </div>
                  <div className="mt-0.5 text-[12px] text-bp-text-dim">
                    {`from activation, every month of the ${figures.termYears}-year term`}
                  </div>
                </div>
                <div
                  data-testid="battery-ai-compute-base"
                  className="font-display text-[17px] font-semibold tracking-[-0.02em] text-bp-text tabular-nums"
                >
                  {formatUsdExact(figures.monthlyBase)}
                </div>
              </div>
              <div className="flex items-baseline justify-between gap-4 border-t border-bp-border py-3.5">
                <div>
                  <div className="text-[14.5px] font-medium text-bp-text">
                    + Network performance bonus<Tag tone="varies">Varies</Tag>
                  </div>
                  <div className="mt-0.5 text-[12px] text-bp-text-dim">
                    the most it can be · may be $0
                  </div>
                </div>
                <div
                  data-testid="battery-ai-compute-bonus-cap"
                  className="font-display text-[17px] font-semibold tracking-[-0.02em] text-bp-text tabular-nums"
                >
                  {formatUsdExact(figures.monthlyBonusCap)}
                </div>
              </div>
              {nodeLoad != null ? (
                <LedgerRow
                  label="Node electricity"
                  note="modeled at 90% utilization · not in your battery savings"
                  value={`${formatNumber(nodeLoad, 0)} kWh/yr`}
                />
              ) : null}
              <div className="flex items-baseline justify-between border-t-2 border-bp-text-soft pt-4">
                <div>
                  <div className="text-[15px] font-semibold text-bp-text">
                    Your monthly payment
                  </div>
                  <div className="mt-0.5 text-[12px] text-bp-text-dim">
                    base + bonus, before taxes
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[13px] font-medium whitespace-nowrap text-bp-text-muted">
                    up to
                  </span>
                  <span
                    data-testid="battery-ai-compute-monthly"
                    className="font-display text-[length:var(--step-4)] leading-none font-semibold tracking-[-0.02em] tabular-nums"
                  >
                    <GradText>{formatUsdExact(figures.monthlyMax)}</GradText>
                  </span>
                </div>
              </div>
              {/* The legend below carries the split for assistive tech. */}
              <div
                aria-hidden="true"
                className="mt-4 flex h-3 gap-0.5 overflow-hidden rounded-full bg-bp-elevated"
              >
                <div
                  className="basis-0 bg-bp-success"
                  style={{ flexGrow: figures.monthlyBase }}
                />
                <div
                  className={clsx(`basis-0`, BONUS_SWATCH)}
                  style={{ flexGrow: figures.monthlyBonusCap }}
                />
              </div>
              <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-bp-text-muted">
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="size-2.5 rounded-sm bg-bp-success"
                  />
                  {formatUsd(figures.monthlyBase)} guaranteed base
                </span>
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={clsx(`size-2.5 rounded-sm`, BONUS_SWATCH)}
                  />
                  up to {formatUsd(figures.monthlyBonusCap)} bonus
                </span>
              </div>
            </div>
            {besidePayment ? <ShareBesidePayment {...besidePayment} /> : null}
          </Panel>

          <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[1.25fr_1fr]">
            <Panel className="grid grid-cols-1 content-center gap-6 rounded-[20px] p-6 sm:grid-cols-3 lg:p-7">
              <Stat
                label="Monthly payment"
                value={`up to ${formatUsd(figures.monthlyMax)}`}
                note={`${formatUsd(figures.monthlyBase)} guaranteed + up to ${formatUsd(figures.monthlyBonusCap)} bonus`}
              />
              <Stat
                label={`${figures.termYears}-year total`}
                value={
                  <span data-testid="battery-ai-compute-term-total">
                    up to {formatUsd(figures.maxTermTotal)}
                  </span>
                }
                note={`${formatUsd(figures.baseTermTotal)} of it guaranteed`}
              />
              <Stat
                label="Term"
                value={`${figures.termYears} yr`}
                note="from activation · no-cost upgrades"
              />
            </Panel>
            <VendorAppCard title="Track your base, bonus and payouts in the Vendor app." />
          </div>
        </>
      ) : (
        <>
          <div data-testid="battery-ai-compute-unavailable" className="mb-3.5">
            <Panel className="rounded-[20px] p-6 text-[length:var(--step-0)] text-bp-text-muted lg:p-8">
              Payment figures unavailable
            </Panel>
          </div>
          <VendorAppCard title="Track your base, bonus and payouts in the Vendor app." />
        </>
      )}

      <AiComputeFaq
        entries={batteryComputeFaq(copy)}
        idPrefix="battery-ai-compute"
      />

      <p
        data-testid="battery-ai-compute-disclosure"
        className="mt-4 max-w-[1100px] text-[11.5px] leading-relaxed text-bp-text-muted"
      >
        {batteryComputeDisclosure(
          nodeLoad != null,
          copy && figures ? { ...copy, planName: figures.planName } : null,
        )}
      </p>
    </Section>
  )
}
